<template>
  <FormContainer :submit="submit">
    <div class="createaccount--wrapper">
      <p class="content-leadin">
        Our reporting system is powered by HomeGauge. Your HomeGauge account will let you
        access your report, create repair lists, and share information with other parties.
      </p>

      <div class="createaccount--form-signin">
        <CreateAccountSigninLink />
      </div>

      <v-layout v-if="apiError" wrap row>
        <v-flex xs12>
          <BaseAlert :hg-value="true" :hg-lead-in="apiError.message" hg-type="error">
            <ul>
              <li v-for="error in apiError.errors" :key="error.field">
                {{error.message}}
              </li>
            </ul>
            <ul v-for="violation in apiError.body.fieldViolations" :key="violation.field">
              <li
                v-for="(error, index) in violation.messages"
                :key="`${violation.field}_${index}`">
                {{error}}
              </li>
            </ul>
          </BaseAlert>
        </v-flex>
      </v-layout>

      <v-layout v-if="apiLookupError" wrap row>
        <v-flex xs12>
          <BaseAlert :hg-value="true" :hg-lead-in="apiLookupError.message" hg-type="error">
            <ul>
              <li v-for="error in apiLookupError.errors" :key="error.field">
                {{error.message}}
              </li>
            </ul>
          </BaseAlert>
        </v-flex>
      </v-layout>

      <FormSectionTitle>Create New Account</FormSectionTitle>

      <div class="createaccount--form-group">
        <CreateAccountFormUser
          v-model="model"
          :loading="loadingExistingAccount"
          :hasExistingAccount="hasExistingAccount"
          @lookupUser="lookupUser"
          @resetUser="resetUser" />

        <CreateAccountFormContact
          v-model="model"
          :disabled="disableInputs"
          @lookupUser="lookupUser" />

        <CreateAccountFormAccount
          v-model="model"
          :disabled="disableInputs"
          @setPasswordError="setPasswordError" />
      </div>
    </div>

    <StepNavigation :continue-disabled="disableSubmitButton" submit-mode @submit="submit" />
  </FormContainer>
</template>

<script>
import { Components, Constants, Mixins } from '@homegauge/vue';

import CreateAccountFormAccount from '@/components/CreateAccountFormAccount.vue';
import CreateAccountFormContact from '@/components/CreateAccountFormContact.vue';
import CreateAccountFormUser from '@/components/CreateAccountFormUser.vue';
import CreateAccountSigninLink from '@/components/CreateAccountSigninLink.vue';
import FormContainer from '@/components/FormContainer.vue';
import FormSectionTitle from '@/components/FormSectionTitle.vue';
import StepNavigation from '@/components/StepNavigation.vue';
import PhoneTypes from '@/constants/phone-types';

const { BaseAlert } = Components;
const { AccountTypes, InputValidations } = Constants;
const { FormValidationsMixin } = Mixins;

export default {
  $_veeValidate: {
    validator: 'new',
  },
  components: {
    BaseAlert,
    CreateAccountFormAccount,
    CreateAccountFormContact,
    CreateAccountFormUser,
    CreateAccountSigninLink,
    FormContainer,
    FormSectionTitle,
    StepNavigation,
  },
  mixins: [FormValidationsMixin],
  props: {
    apiError: {
      type: Object,
      default: null,
    },
    apiLookupError: {
      type: Object,
      default: null,
    },
    hasExistingAccount: {
      type: Boolean,
      default: false,
    },
    loadingCreateAccount: {
      type: Boolean,
      default: false,
    },
    loadingExistingAccount: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      model: {
        company: '',
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        passwordConfirm: '',
        phone: '',
        phoneType: PhoneTypes.mobile.value,
        type: AccountTypes.CUSTOMER,
        username: '',
      },
      inputValidations: InputValidations,
      passwordError: false,
      passwordErrors: [],
      showError: false,
    };
  },
  computed: {
    disableInputs() {
      return this.loadingExistingAccount || this.hasExistingAccount;
    },
    disableSubmitButton() {
      return !!(!this.formRequiredFieldsAreValid
        || this.formHasErrors
        || this.loadingCreateAccount
        || this.loadingExistingAccount
        || this.hasExistingAccount);
    },
  },
  methods: {
    lookupUser() {
      const { email, type } = this.model;
      if (this.loadingExistingAccount || !email || !type) {
        return;
      }

      this.$emit('lookupUser', { email, type });
    },
    async submit() {
      if (this.loadingCreateAccount) {
        return;
      }

      const isValid = await this.$validator.validateAll();
      if (isValid) {
        this.$emit('submit', this.model);
      }
    },
    resetUser() {
      this.$emit('resetUser');
    },
    setPasswordError(value) {
      this.passwordError = value.hasErrors;
      this.passwordErrors = value.errors;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/vars/theme';
@import '../../styles/vars/typography';

.createaccount {
  &--form {
    &-group {
      padding: 1.35rem 0;
    }

    &-signin {
      margin: 24px 0;
    }
  }
}
</style>
